/**
 * QuickstartMobileThankYou
 *
 * Mobile-specific thank you page that shows instructions for adding the app to home screen.
 * Displays different instructions based on device (iOS vs Android).
 * This is shown after the demo ends on mobile devices.
 */

import { useCallback, useEffect, useRef } from 'react';
import { QuickstartCard } from './QuickstartCard';
import { ThemeLogo } from './ThemeLogo';
import { QuickstartTheme, getWishlistButtonImage, getResolvedCtaUrl } from '../types/theme';
import { buildQuickstartSteamUrl } from '../utils/steamUrls';
import { useForcePortrait } from '../utils/responsiveHooks';

interface QuickstartMobileThankYouProps {
  theme: QuickstartTheme;
  autoRedirect?: boolean;
  onRedirect?: (steamUrl: string) => void;
}

export const QuickstartMobileThankYou = ({ theme, autoRedirect, onRedirect }: QuickstartMobileThankYouProps) => {
  const hasAutoRedirected = useRef(false);
  const { shouldRotate } = useForcePortrait();

  const handleCheckItOut = useCallback(() => {
    const ctaUrl = getResolvedCtaUrl(theme);
    if (!ctaUrl) {
      return;
    }

    const finalUrl = buildQuickstartSteamUrl(ctaUrl);

    // Send analytics callback
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Open in new tab instead of redirecting (prevents canceling analytics request)
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  }, [onRedirect, theme]);

  useEffect(() => {
    if (!autoRedirect || hasAutoRedirected.current) {
      return;
    }

    const ctaUrl = getResolvedCtaUrl(theme);
    if (!ctaUrl) {
      return;
    }

    hasAutoRedirected.current = true;

    // Auto-redirect: Use location.href instead of window.open to avoid popup blockers
    const finalUrl = buildQuickstartSteamUrl(ctaUrl);

    // Send analytics callback before redirect
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Delay to ensure analytics can be sent
    setTimeout(() => {
      window.location.href = finalUrl;
    }, 500);
  }, [autoRedirect, onRedirect, theme]);


  return (
    <div
      className='flex items-center justify-center overflow-auto'
      style={
        shouldRotate ? {
          // Landscape mode: force portrait by rotating -90deg
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: '100dvh',
          height: '100dvw',
          transform: 'translate(-50%, -50%) rotate(-90deg)',
          transformOrigin: 'center center',
          backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '1rem',
        } : {
          // Portrait mode: normal layout
          minHeight: '100vh',
          backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '1rem',
        }
      }
    >
      <QuickstartCard theme={theme} showHeader={false}>
        <div className='flex flex-col items-center text-center'>
          {/* Studio Logo */}
          <ThemeLogo kind='studio' theme={theme} className='mt-4' />

          {/* Game Logo - keeps the larger 128px default specific to this mobile screen */}
          <ThemeLogo
            kind='game'
            theme={theme}
            defaultHeight={theme.gameLogoHeight || 128}
            className='mt-8'
          />

          {/* Add to Home Screen Text */}
          <div className='mt-8 font-["Inter"] text-2xl font-semibold' style={{ color: theme.primaryColor }}>
            Add to your home screen!
          </div>

          {/* Add to Home Screen GIF - Placeholder */}
          <div className="mt-6 w-full max-w-sm">
            <img
              src="/quickstart/home-screen.gif"
              alt="Add to home screen instructions"
              className="w-full h-auto"
            />
          </div>

          {/* Wishlist/Store Button */}
          {getResolvedCtaUrl(theme) && (() => {
            const buttonImage = getWishlistButtonImage(theme);
            return (
              <img
                src={buttonImage.src}
                onClick={handleCheckItOut}
                className='mt-6 h-16 cursor-pointer transition-opacity hover:opacity-80'
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCheckItOut();
                  }
                }}
                alt={buttonImage.alt}
              />
            );
          })()}
        </div>
      </QuickstartCard>
    </div>
  );
};
